import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiSearch, FiRefreshCw, FiFilter, FiCalendar,
  FiAlertCircle, FiInfo, FiCheckCircle, FiXCircle,
  FiChevronDown, FiChevronUp, FiDownload, FiHelpCircle,
  FiClock, FiUser, FiMail, FiPhone, FiEye, FiEdit, FiX, 
  FiFileText, FiTag, FiTrendingUp, FiTrendingDown,
  FiBarChart2, FiPieChart, FiGrid, FiList, FiStar,
  FiMessageSquare, FiSend, FiSave, FiPrinter, FiShare2,
  FiFlag, FiUsers, FiArchive, FiZap, FiBell
} from 'react-icons/fi';
import { api } from '../../services/api';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Catégorie pour stats / kanban (l’API peut renvoyer resolu, ferme, annule sans accent). */
function getTicketStatusCategory(status) {
  const s = (status || '').trim().toLowerCase();
  if (s === 'ouvert' || s === 'open' || s === 'nouveau') return 'open';
  if (s === 'en_cours' || s === 'in_progress') return 'in_progress';
  if (s === 'résolu' || s === 'resolu' || s === 'resolved') return 'resolved';
  if (s === 'fermé' || s === 'ferme' || s === 'closed') return 'closed';
  if (s === 'annulé' || s === 'annule' || s === 'cancelled') return 'cancelled';
  return 'other';
}

const TICKET_STATUS_DISPLAY = {
  open: 'Ouvert',
  in_progress: 'En cours',
  resolved: 'Résolu',
  closed: 'Fermé',
  cancelled: 'Annulé',
  other: null
};

function formatTicketStatusForDisplay(status) {
  const cat = getTicketStatusCategory(status);
  if (cat === 'other') return status && String(status).trim() ? status : 'N/A';
  return TICKET_STATUS_DISPLAY[cat] || status || 'N/A';
}

/** Valeur des <option> du select à partir du statut renvoyé par l’API (ex. resolu → résolu). */
function ticketStatusToSelectValue(status) {
  const cat = getTicketStatusCategory(status);
  const map = {
    open: 'ouvert',
    in_progress: 'en_cours',
    resolved: 'résolu',
    closed: 'fermé',
    cancelled: 'annulé',
    other: 'ouvert'
  };
  return map[cat] || 'ouvert';
}

// Obtenir la couleur en fonction du statut
const getStatusColor = (status) => {
  const cat = getTicketStatusCategory(status);
  switch (cat) {
    case 'open':
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
    case 'in_progress':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'resolved':
    case 'closed':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    case 'cancelled':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

const getStatusIcon = (status) => {
  const cat = getTicketStatusCategory(status);
  switch (cat) {
    case 'open':
      return <FiClock className="w-4 h-4" />;
    case 'in_progress':
      return <FiAlertCircle className="w-4 h-4" />;
    case 'resolved':
    case 'closed':
      return <FiCheckCircle className="w-4 h-4" />;
    case 'cancelled':
      return <FiXCircle className="w-4 h-4" />;
    default:
      return <FiInfo className="w-4 h-4" />;
  }
};

// Obtenir la couleur en fonction du type
const getTypeColor = (type) => {
  switch(type?.toLowerCase()) {
    case 'réclamation':
    case 'reclamation':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    case 'question':
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
    case 'remboursement':
    case 'refund':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'technique':
    case 'technical':
      return 'bg-purple-600/20 text-purple-400 border border-purple-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

// Obtenir la couleur en fonction de la priorité
const getPriorityColor = (priority) => {
  switch(priority?.toLowerCase()) {
    case 'haute':
    case 'high':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    case 'moyenne':
    case 'medium':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'basse':
    case 'low':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

/** Libellé FR pour le rôle de l’auteur du ticket (API: user_role). */
function formatTicketUserRole(role) {
  const r = (role || '').toString().trim().toLowerCase();
  if (r === 'client') return 'Client';
  if (r === 'demenageur' || r === 'déménageur' || r === 'mover') return 'Déménageur';
  if (r === 'admin') return 'Admin';
  return role && String(role).trim() ? String(role) : '—';
}

function getUserRoleBadgeClass(role) {
  const r = (role || '').toString().trim().toLowerCase();
  if (r === 'client') return 'bg-sky-600/20 text-sky-300 border border-sky-600/50';
  if (r === 'demenageur' || r === 'déménageur' || r === 'mover') {
    return 'bg-amber-600/20 text-amber-300 border border-amber-600/50';
  }
  if (r === 'admin') return 'bg-violet-600/20 text-violet-300 border border-violet-600/50';
  return 'bg-gray-700 text-gray-300 border border-gray-600';
}

function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [showStats, setShowStats] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    cancelled: 0,
    highPriority: 0,
    avgResolutionTime: 0
  });
  const [admins, setAdmins] = useState([]);
  const [internalNote, setInternalNote] = useState('');
  const [ticketHistory, setTicketHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadTickets();
    loadAdmins();
    // Polling toutes les 30 secondes pour les mises à jour
    const interval = setInterval(loadTickets, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType, filterPriority, filterAssigned, searchTerm]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getAllTickets({
        status: filterStatus,
        type: filterType,
        search: searchTerm
      });
      
      if (response && response.success) {
        const ticketsData = response.data || [];
        setTickets(ticketsData);
        
        // Calculer les statistiques
        calculateStats(ticketsData);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement des tickets');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des tickets:', err);
      setError(err.message || 'Erreur lors du chargement des tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await api.getAllUsers();
      if (response && response.success) {
        const allUsers = response.data || [];
        const adminUsers = allUsers.filter(u => u.role === 'admin' || u.is_admin);
        setAdmins(adminUsers);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des admins:', err);
    }
  };

  const calculateStats = (ticketsData) => {
    const stats = {
      total: ticketsData.length,
      open: 0,
      inProgress: 0,
      resolved: 0,
      closed: 0,
      cancelled: 0,
      highPriority: 0,
      avgResolutionTime: 0
    };

    let totalResolutionTime = 0;
    let resolvedCount = 0;

    ticketsData.forEach(ticket => {
      const cat = getTicketStatusCategory(ticket.status);
      if (cat === 'open') stats.open++;
      else if (cat === 'in_progress') stats.inProgress++;
      else if (cat === 'resolved') {
        stats.resolved++;
        if (ticket.resolved_at && ticket.created_at) {
          const resolutionTime = new Date(ticket.resolved_at) - new Date(ticket.created_at);
          totalResolutionTime += resolutionTime;
          resolvedCount++;
        }
      } else if (cat === 'closed') stats.closed++;
      else if (cat === 'cancelled') stats.cancelled++;

      if (ticket.priority === 'haute' || ticket.priority === 'high') stats.highPriority++;
    });

    if (resolvedCount > 0) {
      stats.avgResolutionTime = Math.round(totalResolutionTime / resolvedCount / (1000 * 60 * 60)); // en heures
    }

    setStats(stats);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
    setInternalNote('');
    // Charger l'historique du ticket
    loadTicketHistory(ticket.id);
  };

  const loadTicketHistory = async (ticketId) => {
    try {
      // Pour l'instant, simuler l'historique
      // Plus tard, créer un endpoint pour récupérer l'historique réel
      setTicketHistory([
        {
          id: '1',
          action: 'Création du ticket',
          user: 'Système',
          timestamp: selectedTicket?.created_at || new Date().toISOString()
        },
        {
          id: '2',
          action: 'Mise à jour du statut',
          user: 'Admin',
          timestamp: selectedTicket?.updated_at || new Date().toISOString()
        }
      ]);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique:', err);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const response = await api.updateTicketStatus(ticketId, newStatus);
      
      if (response && response.success) {
        setTickets(prev => prev.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, status: newStatus, updated_at: new Date().toISOString() }
            : ticket
        ));
        
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(prev => ({ ...prev, status: newStatus, updated_at: new Date().toISOString() }));
        }
        
        setSuccessMessage('Statut mis à jour avec succès');
        setTimeout(() => setSuccessMessage(null), 3000);
        loadTickets();
      } else {
        throw new Error(response?.message || 'Erreur lors de la mise à jour du statut');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      setError(err.message || 'Erreur lors de la mise à jour du statut');
    }
  };

  const handleAssignTicket = async (ticketId, adminId) => {
    try {
      // Simuler l'assignation (créer un endpoint backend si nécessaire)
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, assigned_to: adminId, updated_at: new Date().toISOString() }
          : ticket
      ));
      
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, assigned_to: adminId }));
      }
      
      setSuccessMessage('Ticket assigné avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de l\'assignation:', err);
      setError(err.message || 'Erreur lors de l\'assignation');
    }
  };

  const handleSaveInternalNote = async () => {
    if (!internalNote.trim() || !selectedTicket) return;
    
    try {
      // Simuler la sauvegarde de la note (créer un endpoint backend si nécessaire)
      setSuccessMessage('Note interne sauvegardée');
      setTimeout(() => setSuccessMessage(null), 3000);
      setInternalNote('');
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la note:', err);
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleExportCSV = () => {
    const headers = ['N° Ticket', 'Utilisateur', 'Email', 'Rôle', 'Type', 'Sujet', 'Statut', 'Priorité', 'Date création'];
    const rows = sortedTickets.map(ticket => [
      ticket.ticket_number || `TKT-${String(ticket.id).padStart(3, '0')}`,
      ticket.user_name || 'N/A',
      ticket.user_email || 'N/A',
      formatTicketUserRole(ticket.user_role),
      ticket.type || 'N/A',
      ticket.subject || 'N/A',
      formatTicketStatusForDisplay(ticket.status),
      ticket.priority || 'N/A',
      formatDate(ticket.created_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tickets_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleViewQuote = async (quoteId) => {
    try {
      const response = await api.getAllReservations();
      
      if (response && response.success) {
        const moves = response.data || [];
        const moveWithQuote = moves.find(move =>
          move.quote_id === quoteId ||
          move.id === quoteId ||
          (Array.isArray(move.group_quote_ids) && move.group_quote_ids.includes(quoteId))
        );
        
        if (moveWithQuote) {
          navigate(`/admin/moves/${moveWithQuote.id}`);
          setShowDetailsModal(false);
        } else {
          alert('Déménagement associé au devis non trouvé.');
        }
      } else {
        throw new Error('Erreur lors du chargement des déménagements');
      }
    } catch (err) {
      console.error('Erreur lors de la recherche du devis:', err);
      alert('Erreur lors de la recherche du devis associé');
    }
  };

  // Trier les tickets
  const sortedTickets = [...tickets].filter(ticket => {
    if (filterPriority !== 'all') {
      const priority = ticket.priority?.toLowerCase();
      if (filterPriority === 'high' && priority !== 'haute' && priority !== 'high') return false;
      if (filterPriority === 'medium' && priority !== 'moyenne' && priority !== 'medium') return false;
      if (filterPriority === 'low' && priority !== 'basse' && priority !== 'low') return false;
    }
    if (filterAssigned !== 'all') {
      if (filterAssigned === 'assigned' && !ticket.assigned_to) return false;
      if (filterAssigned === 'unassigned' && ticket.assigned_to) return false;
    }
    return true;
  }).sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'created_at' || sortField === 'updated_at') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else {
      aValue = String(aValue ?? '').toLowerCase();
      bValue = String(bValue ?? '').toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Données pour les graphiques
  const statusChartData = {
    labels: ['Ouvert', 'En cours', 'Résolu', 'Fermé', 'Annulé'],
    datasets: [{
      label: 'Tickets par statut',
      data: [
        stats.open,
        stats.inProgress,
        stats.resolved,
        stats.closed,
        stats.cancelled
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.5)',
        'rgba(234, 179, 8, 0.5)',
        'rgba(34, 197, 94, 0.5)',
        'rgba(107, 114, 128, 0.5)',
        'rgba(239, 68, 68, 0.5)'
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(234, 179, 8, 1)',
        'rgba(34, 197, 94, 1)',
        'rgba(107, 114, 128, 1)',
        'rgba(239, 68, 68, 1)'
      ],
      borderWidth: 2
    }]
  };

  const typeChartData = {
    labels: ['Réclamation', 'Question', 'Remboursement', 'Technique'],
    datasets: [{
      label: 'Tickets par type',
      data: [
        tickets.filter(t => t.type?.toLowerCase().includes('réclamation') || t.type?.toLowerCase().includes('reclamation')).length,
        tickets.filter(t => t.type?.toLowerCase().includes('question')).length,
        tickets.filter(t => t.type?.toLowerCase().includes('remboursement') || t.type?.toLowerCase().includes('refund')).length,
        tickets.filter(t => t.type?.toLowerCase().includes('technique') || t.type?.toLowerCase().includes('technical')).length
      ],
      backgroundColor: [
        'rgba(239, 68, 68, 0.5)',
        'rgba(59, 130, 246, 0.5)',
        'rgba(234, 179, 8, 0.5)',
        'rgba(168, 85, 247, 0.5)'
      ],
      borderColor: [
        'rgba(239, 68, 68, 1)',
        'rgba(59, 130, 246, 1)',
        'rgba(234, 179, 8, 1)',
        'rgba(168, 85, 247, 1)'
      ],
      borderWidth: 2
    }]
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-screen bg-gray-900">
        <div className="w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
        <p className="mt-4 text-yellow-500 font-medium">Chargement des tickets...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiTag className="w-8 h-8 mr-3" />
            Tickets de support
          </h1>
          <p className="text-gray-400">Gérez les demandes et réclamations des utilisateurs</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
            title={viewMode === 'list' ? 'Vue Kanban' : 'Vue Liste'}
          >
            {viewMode === 'list' ? <FiGrid className="w-5 h-5" /> : <FiList className="w-5 h-5" />}
          </button>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
          >
            <FiDownload className="mr-2" />
            Exporter CSV
          </button>
          <button
            onClick={loadTickets}
            disabled={loading}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
          >
            <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Messages d'alerte/succès */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiCheckCircle className="w-5 h-5 mr-2" />
            {successMessage}
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-300">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Statistiques */}
      {showStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Total</p>
              <FiTag className="w-5 h-5 text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{stats.total}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-600/10 to-blue-600/5 border border-blue-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Ouverts</p>
              <FiClock className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-blue-400">{stats.open}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-600/10 to-yellow-600/5 border border-yellow-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">En cours</p>
              <FiAlertCircle className="w-5 h-5 text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{stats.inProgress}</p>
          </div>
          <div className="bg-gradient-to-br from-green-600/10 to-green-600/5 border border-green-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Résolus</p>
              <FiCheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-green-400">{stats.resolved}</p>
          </div>
          <div className="bg-gradient-to-br from-gray-600/10 to-gray-600/5 border border-gray-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Fermés</p>
              <FiArchive className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-400">{stats.closed}</p>
          </div>
          <div className="bg-gradient-to-br from-red-600/10 to-red-600/5 border border-red-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Priorité haute</p>
              <FiFlag className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-3xl font-bold text-red-400">{stats.highPriority}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-600/10 to-purple-600/5 border border-purple-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Temps moyen</p>
              <FiClock className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-purple-400">{stats.avgResolutionTime}h</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-600/10 to-indigo-600/5 border border-indigo-600/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Taux résolution</p>
              <FiTrendingUp className="w-5 h-5 text-indigo-400" />
            </div>
            <p className="text-3xl font-bold text-indigo-400">
              {stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0}%
            </p>
          </div>
        </div>
      )}

      {/* Graphiques */}
      {showStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <FiPieChart className="w-5 h-5 mr-2 text-yellow-400" />
              Répartition par statut
            </h3>
            <div className="h-64">
              <Doughnut data={statusChartData} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: '#e5e7eb' }
                  }
                }
              }} />
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <FiBarChart2 className="w-5 h-5 mr-2 text-yellow-400" />
              Répartition par type
            </h3>
            <div className="h-64">
              <Bar data={typeChartData} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#e5e7eb' },
                    grid: { color: '#374151' }
                  },
                  x: {
                    ticks: { color: '#e5e7eb' },
                    grid: { color: '#374151' }
                  }
                }
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Filtres et recherche */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Recherche */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          {/* Filtre par statut */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="ouvert">Ouvert</option>
            <option value="en_cours">En cours</option>
            <option value="résolu">Résolu</option>
            <option value="fermé">Fermé</option>
            <option value="annulé">Annulé</option>
          </select>

          {/* Filtre par type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="all">Tous les types</option>
            <option value="réclamation">Réclamation</option>
            <option value="question">Question</option>
            <option value="remboursement">Remboursement</option>
            <option value="technique">Technique</option>
          </select>

          {/* Filtre par priorité */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="all">Toutes les priorités</option>
            <option value="high">Haute</option>
            <option value="medium">Moyenne</option>
            <option value="low">Basse</option>
          </select>

          {/* Filtre par assignation */}
          <select
            value={filterAssigned}
            onChange={(e) => setFilterAssigned(e.target.value)}
            className="px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="all">Tous</option>
            <option value="assigned">Assignés</option>
            <option value="unassigned">Non assignés</option>
          </select>
        </div>
      </div>

      {/* Table des tickets */}
      {viewMode === 'list' ? (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400"
                    onClick={() => handleSort('ticket_number')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>N° Ticket</span>
                      {sortField === 'ticket_number' && (
                        sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400"
                    onClick={() => handleSort('user_name')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Utilisateur</span>
                      {sortField === 'user_name' && (
                        sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400"
                    onClick={() => handleSort('user_role')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Rôle</span>
                      {sortField === 'user_role' && (
                        sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                    Sujet
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                    Priorité
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Date</span>
                      {sortField === 'created_at' && (
                        sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {sortedTickets.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-400">
                      <FiHelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun ticket trouvé</p>
                    </td>
                  </tr>
                ) : (
                  sortedTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-700/50 transition-all duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-yellow-400">
                          {ticket.ticket_number || `TKT-${String(ticket.id).padStart(3, '0')}`}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-white">
                          {ticket.user_name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {ticket.user_email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getUserRoleBadgeClass(ticket.user_role)}`}
                        >
                          {formatTicketUserRole(ticket.user_role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(ticket.type)}`}>
                          {ticket.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white max-w-xs truncate">
                          {ticket.subject || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {ticket.priority && (
                          <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                            <FiFlag className="w-3 h-3 mr-1" />
                            {ticket.priority}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1">{formatTicketStatusForDisplay(ticket.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                        <button
                          onClick={() => handleViewDetails(ticket)}
                          className="text-yellow-400 hover:text-yellow-300 hover:underline transition-colors"
                          title="Voir les détails"
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Vue Kanban
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { label: 'ouvert', cat: 'open' },
            { label: 'en_cours', cat: 'in_progress' },
            { label: 'résolu', cat: 'resolved' },
            { label: 'fermé', cat: 'closed' },
            { label: 'annulé', cat: 'cancelled' }
          ].map(({ label, cat }) => {
            const statusTickets = sortedTickets.filter(t => getTicketStatusCategory(t.status) === cat);
            
            return (
              <div key={label} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center justify-between">
                  <span className="capitalize">{label.replace(/_/g, ' ')}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(label)}`}>
                    {statusTickets.length}
                  </span>
                </h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {statusTickets.map(ticket => (
                    <div
                      key={ticket.id}
                      onClick={() => handleViewDetails(ticket)}
                      className="bg-gray-700/50 border border-gray-600 rounded-lg p-3 cursor-pointer hover:bg-gray-700 hover:border-yellow-600/50 transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-semibold text-yellow-400">
                          {ticket.ticket_number || `TKT-${String(ticket.id).padStart(3, '0')}`}
                        </span>
                        {ticket.priority && (
                          <span className={`px-2 py-0.5 text-xs rounded ${getPriorityColor(ticket.priority)}`}>
                            <FiFlag className="w-3 h-3 inline" />
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white font-medium mb-1 line-clamp-2">
                        {ticket.subject || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-400 mb-1">
                        {ticket.user_name || 'N/A'}
                      </p>
                      <p className="text-xs mb-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full font-medium capitalize ${getUserRoleBadgeClass(ticket.user_role)}`}
                        >
                          {formatTicketUserRole(ticket.user_role)}
                        </span>
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-0.5 text-xs rounded ${getTypeColor(ticket.type)}`}>
                          {ticket.type || 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-yellow-400">
                Ticket {selectedTicket.ticket_number || `TKT-${String(selectedTicket.id).replace(/-/g, '').substring(0, 8).toUpperCase()}`}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Informations utilisateur */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FiUser className="mr-2 text-yellow-400" />
                  Informations utilisateur
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Nom</p>
                    <p className="text-white font-medium">{selectedTicket.user_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <p className="text-white font-medium">{selectedTicket.user_email || 'N/A'}</p>
                  </div>
                  {selectedTicket.user_phone && (
                    <div>
                      <p className="text-sm text-gray-400">Téléphone</p>
                      <p className="text-white font-medium">{selectedTicket.user_phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-400">Rôle</p>
                    <span
                      className={`mt-1 inline-flex px-3 py-1 text-xs font-semibold rounded-full capitalize ${getUserRoleBadgeClass(selectedTicket.user_role)}`}
                    >
                      {formatTicketUserRole(selectedTicket.user_role)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Informations ticket */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FiInfo className="mr-2 text-yellow-400" />
                  Informations ticket
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Type</p>
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(selectedTicket.type)}`}>
                      {selectedTicket.type || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Statut</p>
                    <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedTicket.status)}`}>
                      {getStatusIcon(selectedTicket.status)}
                      <span className="ml-1">{formatTicketStatusForDisplay(selectedTicket.status)}</span>
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Priorité</p>
                    {selectedTicket.priority ? (
                      <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getPriorityColor(selectedTicket.priority)}`}>
                        <FiFlag className="w-3 h-3 mr-1" />
                        {selectedTicket.priority}
                      </span>
                    ) : (
                      <span className="text-white">Non définie</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Assigné à</p>
                    {selectedTicket.assigned_to ? (
                      <select
                        value={selectedTicket.assigned_to}
                        onChange={(e) => handleAssignTicket(selectedTicket.id, e.target.value)}
                        className="mt-1 px-3 py-1 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      >
                        {admins.map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.first_name} {admin.last_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value=""
                        onChange={(e) => handleAssignTicket(selectedTicket.id, e.target.value)}
                        className="mt-1 px-3 py-1 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      >
                        <option value="">Non assigné</option>
                        {admins.map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.first_name} {admin.last_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Date de création</p>
                    <p className="text-white font-medium">{formatDate(selectedTicket.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Dernière mise à jour</p>
                    <p className="text-white font-medium">{formatDate(selectedTicket.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Sujet et description */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Sujet</h3>
                <p className="text-white mb-4">{selectedTicket.subject || 'N/A'}</p>
                
                <h3 className="text-lg font-semibold text-white mb-4">Description</h3>
                <p className="text-white whitespace-pre-wrap">{selectedTicket.description || 'N/A'}</p>
              </div>

              {/* Note interne */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FiMessageSquare className="mr-2 text-yellow-400" />
                  Note interne
                </h3>
                <textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Ajouter une note interne (visible uniquement par les admins)..."
                  rows="4"
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
                <button
                  onClick={handleSaveInternalNote}
                  className="mt-2 px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center"
                >
                  <FiSave className="mr-2" />
                  Sauvegarder
                </button>
              </div>

              {/* Historique */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center">
                    <FiClock className="mr-2 text-yellow-400" />
                    Historique
                  </h3>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-gray-400 hover:text-yellow-400"
                  >
                    {showHistory ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>
                {showHistory && (
                  <div className="space-y-2">
                    {ticketHistory.map(entry => (
                      <div key={entry.id} className="flex items-start space-x-3 p-2 bg-gray-900/50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm text-white">{entry.action}</p>
                          <p className="text-xs text-gray-400">{entry.user} • {formatDate(entry.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Informations liées */}
              {selectedTicket.quote_id && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <FiFileText className="mr-2 text-yellow-400" />
                    Devis associé
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">ID du devis</p>
                      <p className="text-white font-medium font-mono text-sm">{selectedTicket.quote_id}</p>
                    </div>
                    <button
                      onClick={() => handleViewQuote(selectedTicket.quote_id)}
                      className="flex items-center px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold shadow-lg"
                    >
                      <FiFileText className="mr-2" />
                      Voir le devis
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700">
                <select
                  value={ticketStatusToSelectValue(selectedTicket.status)}
                  onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="ouvert">Ouvert</option>
                  <option value="en_cours">En cours</option>
                  <option value="résolu">Résolu</option>
                  <option value="fermé">Fermé</option>
                  <option value="annulé">Annulé</option>
                </select>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-5 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTickets;
